<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useSession, signOut } from '@/auth';
import { useSidebar } from '@/composables/useSidebar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { PanelLeft, PanelLeftOpen, Settings, LogOut } from 'lucide-vue-next';

const router = useRouter();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const { collapsed, toggle } = useSidebar();

async function handleSignOut() {
  try {
    await signOut();
    router.push('/sign-in');
  } catch (e) {
    console.error('Sign out failed:', e);
  }
}
</script>

<template>
  <div class="h-screen bg-background text-foreground flex overflow-hidden">
    <!-- Sidebar -->
    <aside
      v-if="!collapsed"
      class="w-64 shrink-0 border-r border-border bg-sidebar hidden md:flex flex-col"
    >
      <!-- Brand row -->
      <div class="px-3 py-3 flex items-center justify-between">
        <button
          class="text-base font-mono font-bold text-foreground hover:text-primary transition-colors"
          @click="router.push('/')"
        >
          hone.study
        </button>
        <Button
          variant="ghost"
          size="icon"
          class="size-8"
          aria-label="Collapse sidebar"
          @click="toggle"
        >
          <PanelLeft class="size-4" />
        </Button>
      </div>

      <!-- Nav content -->
      <div class="flex-1 overflow-y-auto scrollbar-thin">
        <slot name="nav" />
      </div>

      <!-- User menu -->
      <div
        v-if="user"
        class="border-t border-border p-2"
      >
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <button class="flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm hover:bg-accent transition-colors">
              <div class="size-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium shrink-0">
                {{ (user.name || user.email || '?').slice(0, 1).toUpperCase() }}
              </div>
              <span class="flex-1 truncate text-left text-foreground">{{ user.name || user.email }}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="top"
          >
            <DropdownMenuItem @click="router.push('/settings')">
              <Settings class="size-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              @click="handleSignOut"
            >
              <LogOut class="size-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>

    <!-- Main area -->
    <main class="flex-1 flex flex-col min-w-0">
      <!-- Desktop: expand-sidebar button when collapsed -->
      <div
        v-if="collapsed"
        class="hidden md:flex px-3 py-2 shrink-0"
      >
        <Button
          variant="ghost"
          size="icon"
          class="size-8"
          aria-label="Expand sidebar"
          @click="toggle"
        >
          <PanelLeftOpen class="size-4" />
        </Button>
      </div>
      <!-- Mobile top bar slot (for views that need it) -->
      <slot name="mobile-top" />
      <slot />
    </main>
  </div>
</template>
